package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.pages.recommendations.RecommendationsFeedback
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextArea

/**
 * FeedbackSection component that displays the feedback form with emoji ratings and comment box
 * Matches the design from the provided image while respecting existing UI patterns
 */
@Composable
fun FeedbackSection(
    feedback: RecommendationsFeedback,
    onFeedbackChange: (RecommendationsFeedback) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(Color.transparent)
            .borderRadius(16.px)
            .border(1.px, LineStyle.Solid, AppTheme.colors.accent)
            .boxShadow(0.px, 2.px, 8.px, color = Color("rgba(0, 0, 0, 0.06)"))
            .padding(24.px),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(24.px)
    ) {
        // Header with icon and title
        Row(
            horizontalArrangement = Arrangement.spacedBy(12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = "Feedback",
                modifier = AppTheme.typography.h2.toModifier()
                    .color(AppTheme.colors.text)
            )
        }

        // Question 1: Usefulness
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(12.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                text = "Ți s-au părut utile recomandările?",
                modifier = AppTheme.typography.body1Medium.toModifier()
                    .color(AppTheme.colors.text)
                    .textAlign(TextAlign.Center)
            )

            EmojiRating(
                selectedRating = feedback.usefulScore,
                onRatingSelected = { rating ->
                    onFeedbackChange(feedback.copy(usefulScore = rating))
                }
            )

            Spacer(Modifier.height(4.px))
        }

        // Question 2: Expectations
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(12.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                text = "Recomandările au fost conform așteptărilor tale?",
                modifier = AppTheme.typography.body1Medium.toModifier()
                    .color(AppTheme.colors.text)
                    .textAlign(TextAlign.Center)
            )

            EmojiRating(
                selectedRating = feedback.expectationScore,
                onRatingSelected = { rating ->
                    onFeedbackChange(feedback.copy(expectationScore = rating))
                }
            )

            Spacer(Modifier.height(4.px))
        }

        // Comment box
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(8.px),
            horizontalAlignment = Alignment.Start
        ) {
            TextArea(
                attrs = {
                    attr("placeholder", "Părerea ta despre Quiz-ul de Orientare sau Recomandări")
                    value(feedback.comment ?: "")
                    onInput { event ->
                        val newComment = event.target.asDynamic().value as String
                        onFeedbackChange(feedback.copy(comment = newComment.takeIf { it.isNotBlank() }))
                    }
                    style {
                        width(100.percent)
                        minHeight(100.px)
                        padding(12.px, 16.px)
                        border(1.px, LineStyle.Solid, Color("rgba(0, 0, 0, 0.12)"))
                        borderRadius(8.px)
                        fontSize(14.px)
                        fontFamily("inherit")
                        lineHeight(1.5.em)
                        property("resize", "vertical")
                        outline("none")

                        property("transition", "border-color 200ms ease-in-out")
                    }
                    onFocus {
                        it.target.asDynamic().style.borderColor = "#10b981"
                    }
                    onBlur {
                        it.target.asDynamic().style.borderColor = "rgba(0, 0, 0, 0.12)"
                    }
                }
            )
        }

        // Helpful note
        SpanText(
            text = "Feedback-ul tău ne ajută să îmbunătățim Quiz-ul pentru rezultate cât mai relevante pentru tine.",
            modifier = AppTheme.typography.body2.toModifier()
                .color(AppTheme.colors.textTertiary)
                .lineHeight(1.5)
                .textAlign(TextAlign.Start)
        )
    }
}
